/**
 * WFSLayer
 *
 * Implementation for WFS (Web Feature Service) vector layers.
 * WFS layers fetch vector data from a WFS server and render as GeoJSON.
 *
 * Expected config structure:
 * {
 *   "typeName": "workspace:layername",     // WFS typename (layer name)
 *   "version": "2.0.0",                    // WFS version (1.0.0, 1.1.0, 2.0.0)
 *   "outputFormat": "application/json",    // Output format
 *   "maxFeatures": 1000,                   // Max features to fetch (optional)
 *   "crs": "EPSG:4326",                    // Coordinate reference system
 *   "propertyName": ["id", "name"],        // Properties to fetch (optional)
 *   "cqlFilter": "status='active'",        // CQL filter (optional)
 *   "style": {                             // Mapbox GL style
 *     "type": "circle",                    // circle, line, fill, symbol
 *     "paint": { ... }                     // Mapbox paint properties
 *   }
 * }
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { loadFaIconToMap, getIconId, getDefaultIcon } from './utils/iconUtils';
import { visibilityAwareInterval, VisibilityAwareIntervalHandle } from './utils/visibilityAwareInterval';
import { isInRefreshTimeRange } from './utils/refreshTimeRange';
import { logger } from '../../utils/logger';
import { getBackendUrl } from '../../services/configService';
import './WFSLayer.scss';

const log = logger('WFSLayer');

export class WFSLayer extends BaseLayer {
  /**
   * Mapbox source ID for this layer
   */
  private sourceId: string;

  /**
   * Mapbox layer ID for this layer
   */
  private layerId: string;

  /**
   * GeoJSON data cache
   */
  private geojsonData: GeoJSON.FeatureCollection | null = null;

  /**
   * Auto-refresh handle (visibility-aware).
   */
  private refreshHandle: VisibilityAwareIntervalHandle | null = null;

  /**
   * Click event handler reference for cleanup
   */
  private clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;

  /**
   * Current popup reference
   */
  private currentPopup: mapboxgl.Popup | null = null;

  /**
   * Current icon ID for symbol layers
   */
  private currentIconId: string | null = null;

  /**
   * Constructor
   *
   * @param map Mapbox GL map instance
   * @param config Layer configuration from database
   */
  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);
    this.sourceId = `wfs-source-${config.id}`;
    this.layerId = `wfs-layer-${config.id}`;
  }

  /**
   * Initialize WFS layer
   */
  protected async onInitialize(): Promise<void> {
    log.debug(` Initializing layer: ${this.config.name}`);

    // Validate required config
    if (!this.config.source_url) {
      throw new Error('WFS layer requires source_url');
    }

    if (!this.config.config.typeName) {
      throw new Error('WFS layer requires config.typeName');
    }

    log.debug(` Config validated - source_url: ${this.config.source_url}, typeName: ${this.config.config.typeName}`);

    // Try to fetch initial data but don't fail if it's not available
    try {
      await this.fetchData();
      log.debug(` Initial data fetch successful for ${this.config.name}`);
    } catch (error) {
      log.warn(` Initial data fetch failed for ${this.config.name}, will retry on enable:`, error);
      // Initialize with empty data - will retry on enable
      this.geojsonData = { type: 'FeatureCollection', features: [] };
    }
  }

  /**
   * Enable WFS layer on map
   */
  protected async onEnable(): Promise<void> {
    log.debug(` Enabling layer: ${this.config.name}`);

    // If we have no data or empty features, try to fetch now
    if (!this.geojsonData || !this.geojsonData.features || this.geojsonData.features.length === 0) {
      log.debug(` No data available, fetching now...`);
      try {
        await this.fetchData();
      } catch (error) {
        log.error(` Failed to fetch data on enable:`, error);
        throw new Error(`Cannot enable layer without data: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    log.debug(` Adding source with ${this.geojsonData?.features?.length || 0} features`);

    // Load icon if this is a symbol layer
    const style = this.config.config.style || {};
    if (style.type === 'symbol') {
      const iconName = style.icon || getDefaultIcon(this.config.name);
      const iconColor = style.paint?.['icon-color'] || '#3b82f6';
      const iconSize = style.layout?.['icon-size'] || style.paint?.['icon-size'] || 1.5;
      // Halo/outline settings for accessibility (especially on satellite backgrounds)
      const iconHaloColor = style.haloColor || style.paint?.['icon-halo-color'];
      const iconHaloWidth = style.haloWidth ?? style.paint?.['icon-halo-width'] ?? 0;
      const iconShadowBlur = style.shadowBlur ?? 0;
      const iconShadowColor = style.shadowColor;

      try {
        this.currentIconId = await loadFaIconToMap(this.map, iconName, {
          color: iconColor,
          size: iconSize,
          haloColor: iconHaloColor,
          haloWidth: iconHaloWidth,
          shadowBlur: iconShadowBlur,
          shadowColor: iconShadowColor
        });
        log.debug(` Loaded icon: ${this.currentIconId}`);
      } catch (error) {
        log.error(` Failed to load icon ${iconName}, falling back to circle:`, error);
        // Fall back to circle if icon fails
        this.config.config.style = { ...style, type: 'circle' };
      }
    }

    // Add source if it doesn't exist
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: this.geojsonData || { type: 'FeatureCollection', features: [] }
      });
      log.debug(` Source added: ${this.sourceId}`);
    } else {
      // Update existing source with fresh data
      const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
      if (source && this.geojsonData) {
        source.setData(this.geojsonData);
        log.debug(` Source updated with fresh data`);
      }
    }

    // Add layer if it doesn't exist, or rebuild it if it exists
    // (rebuilding ensures style changes are applied)
    if (!this.map.getLayer(this.layerId)) {
      const layerDef = this.buildLayerDefinition();
      log.debug(` Adding layer to map:`, layerDef);
      this.map.addLayer(layerDef);
      log.debug(` Layer added: ${this.layerId}`);
    } else {
      // Layer exists - remove and re-add to apply any style changes
      log.debug(` Layer exists, rebuilding to apply any style changes`);
      this.map.removeLayer(this.layerId);
      const layerDef = this.buildLayerDefinition();
      log.debug(` Re-adding layer to map:`, layerDef);
      this.map.addLayer(layerDef);
      log.debug(` Layer rebuilt with updated styles`);
    }

    // Stale-window guard: the early "fetch if empty" check above keeps the
    // source non-empty for a snappy first render, but data captured at app
    // load can be hours stale by the time the user toggles the layer on.
    // Fire a background refresh so changes catch up without waiting for the
    // next auto-refresh tick.
    this.refresh().catch((err) =>
      log.warn(` onEnable refresh failed for ${this.config.name}:`, err),
    );

    // Start auto-refresh if configured
    const refreshInterval = this.config.config.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      this.startAutoRefresh(refreshInterval);
      log.debug(` Auto-refresh started: ${refreshInterval}ms`);
    }

    // Set up click event handler for feature popups
    this.setupClickHandler();

    log.debug(` Layer ${this.config.name} enabled successfully`);
  }

  /**
   * Disable WFS layer on map
   */
  protected async onDisable(): Promise<void> {
    if (this.map.getLayer(this.layerId)) {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'none');
    }

    // Stop auto-refresh
    this.stopAutoRefresh();

    // Remove click handler and popup
    this.cleanupClickHandler();
  }

  /**
   * Update layer opacity
   *
   * @param opacity Opacity value (0-1)
   */
  protected async onSetOpacity(opacity: number): Promise<void> {
    if (!this.map.getLayer(this.layerId)) return;

    const layerType = this.config.config.style?.type || 'circle';

    // Update opacity based on layer type
    switch (layerType) {
      case 'circle':
        this.map.setPaintProperty(this.layerId, 'circle-opacity', opacity);
        break;
      case 'line':
        this.map.setPaintProperty(this.layerId, 'line-opacity', opacity);
        break;
      case 'fill':
        this.map.setPaintProperty(this.layerId, 'fill-opacity', opacity);
        break;
      case 'symbol':
        this.map.setPaintProperty(this.layerId, 'icon-opacity', opacity);
        this.map.setPaintProperty(this.layerId, 'text-opacity', opacity);
        break;
    }
  }

  /**
   * Update layer order
   *
   * @param order Order value (higher = on top)
   */
  protected onSetOrder(order: number): void {
    // Move layer to appropriate position
    // Implementation similar to WMSLayer
  }

  /**
   * Destroy layer and clean up resources
   */
  protected async onDestroy(): Promise<void> {
    // Stop auto-refresh
    this.stopAutoRefresh();

    // Remove click handler and popup
    this.cleanupClickHandler();

    // Remove layer
    if (this.map.getLayer(this.layerId)) {
      this.map.removeLayer(this.layerId);
    }

    // Remove source
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    // Clear data cache
    this.geojsonData = null;
  }

  /**
   * Get Mapbox layer IDs
   */
  public getMapboxLayerIds(): string[] {
    return [this.layerId];
  }

  /**
   * Get Mapbox source IDs
   */
  public getMapboxSourceIds(): string[] {
    return [this.sourceId];
  }

  /**
   * Manually refresh layer data
   */
  public async refresh(): Promise<void> {
    await this.fetchData();

    // Update source data if layer is enabled
    if (this.state.isEnabled) {
      const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
      if (source && this.geojsonData) {
        source.setData(this.geojsonData);
      }
    }
  }

  /**
   * Fetch data from WFS server
   */
  private async fetchData(): Promise<void> {
    const url = this.buildWFSUrl();

    log.debug(` Fetching WFS data from: ${url}`);

    try {
      const response = await fetch(url, {
        credentials: 'include', // Include auth cookies for backend proxy
      });

      log.debug(` WFS response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        log.error(` WFS request failed: ${response.statusText}`, errorText);
        throw new Error(`WFS request failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Validate GeoJSON
      if (!data || data.type !== 'FeatureCollection') {
        log.error(` Invalid GeoJSON response:`, data);
        throw new Error('Invalid GeoJSON response from WFS server');
      }

      log.debug(` Successfully fetched ${data.features?.length || 0} features`);
      this.geojsonData = data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error(` Error fetching WFS data:`, error);
      throw new Error(`Failed to fetch WFS data: ${errorMessage}`);
    }
  }

  /**
   * Build WFS request URL
   *
   * @returns WFS GetFeature URL
   */
  private buildWFSUrl(): string {
    // Get backend URL for production/staging (where frontend and backend are on different domains)
    const backendUrl = getBackendUrl() || '';

    // Use the source_url as configured
    // If it already starts with /api/geoserver, it's already a proxy URL
    let baseUrl = this.config.source_url!;

    log.debug(` buildWFSUrl - Original source_url: ${baseUrl}`);

    // Only modify the URL if it's an external GeoServer URL (not already proxied)
    if (!baseUrl.startsWith('/api/geoserver') && !baseUrl.startsWith('http://localhost') && !baseUrl.startsWith('http://127.0.0.1')) {
      // External URL - needs proxying through backend
      if (baseUrl.includes('intellifirenwt.com') || baseUrl.includes('geoserver')) {
        // Use relative proxy path (will resolve to current origin)
        baseUrl = '/api/geoserver/geoserver/wfs';
        log.debug(` buildWFSUrl - Rewritten to proxy: ${baseUrl}`);
      }
    } else {
      log.debug(` buildWFSUrl - Already a proxy URL, using as-is`);
    }

    // CRITICAL: In production/staging, prepend backend URL to relative paths
    // This fixes the issue where relative URLs resolve to frontend origin instead of backend
    if (baseUrl.startsWith('/') && backendUrl) {
      baseUrl = backendUrl + baseUrl;
      log.debug(` buildWFSUrl - Prepended backendUrl: ${baseUrl}`);
    }

    const config = this.config.config;

    // Build query parameters
    const params = new URLSearchParams({
      SERVICE: 'WFS',
      VERSION: config.version || '2.0.0',
      REQUEST: 'GetFeature',
      TYPENAME: config.typeName,
      OUTPUTFORMAT: config.outputFormat || 'application/json'
    });

    // Add optional parameters
    if (config.maxFeatures) {
      params.append('MAXFEATURES', String(config.maxFeatures));
    }

    if (config.srsName) {
      params.append('SRSNAME', config.srsName);
    }

    if (config.propertyName && Array.isArray(config.propertyName)) {
      params.append('PROPERTYNAME', config.propertyName.join(','));
    }

    if (config.cqlFilter) {
      params.append('CQL_FILTER', config.cqlFilter);
    }

    // Add any custom parameters
    if (config.customParams) {
      Object.entries(config.customParams).forEach(([key, value]) => {
        params.append(key, String(value));
      });
    }

    // Build final URL
    const separator = baseUrl.includes('?') ? '&' : '?';
    const finalUrl = `${baseUrl}${separator}${params.toString()}`;
    log.debug(` buildWFSUrl - Final URL: ${finalUrl}`);
    return finalUrl;
  }

  /**
   * Build Mapbox layer definition
   *
   * @returns Layer definition
   */
  private buildLayerDefinition(): mapboxgl.AnyLayer {
    const config = this.config.config;
    const style = config.style || {};
    const layerType = style.type || 'circle';

    // Base layer definition
    const baseDef = {
      id: this.layerId,
      type: layerType as any,
      source: this.sourceId,
      minzoom: this.config.zoom_min,
      maxzoom: this.config.zoom_max
    };

    // Default paint styles by type
    const defaultPaint: Record<string, any> = {
      circle: {
        'circle-radius': 6,
        'circle-color': '#3b82f6',
        'circle-opacity': this.state.currentOpacity,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      },
      line: {
        'line-color': '#3b82f6',
        'line-width': 2,
        'line-opacity': this.state.currentOpacity
      },
      fill: {
        'fill-color': '#3b82f6',
        'fill-opacity': this.state.currentOpacity * 0.5,
        'fill-outline-color': '#1e40af'
      },
      symbol: {
        'icon-opacity': this.state.currentOpacity,
        'text-opacity': this.state.currentOpacity
      }
    };

    // Default layout styles by type
    const defaultLayout: Record<string, any> = {
      circle: { visibility: 'visible' },
      line: { visibility: 'visible' },
      fill: { visibility: 'visible' },
      symbol: {
        visibility: 'visible',
        'icon-image': this.currentIconId || '',
        'icon-size': style.layout?.['icon-size'] || style.paint?.['icon-size'] || 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': false
      }
    };

    // Merge default paint with custom paint
    // Filter out layout properties that were incorrectly placed in paint
    const { 'icon-size': _iconSize, ...filteredPaint } = style.paint || {};
    const paint = {
      ...defaultPaint[layerType],
      ...filteredPaint
    };

    // Merge default layout with custom layout
    const layout = {
      ...defaultLayout[layerType],
      ...style.layout
    };

    return {
      ...baseDef,
      paint,
      layout
    } as mapboxgl.AnyLayer;
  }

  /**
   * Start auto-refresh timer. Visibility-aware (pauses in background tabs).
   * Gated by refreshTimeRange when configured.
   *
   * @param interval Refresh interval in milliseconds
   */
  private startAutoRefresh(interval: number): void {
    this.stopAutoRefresh();

    this.refreshHandle = visibilityAwareInterval(() => {
      const range = this.config.config.refreshTimeRange;
      if (range && !isInRefreshTimeRange(range)) return;
      this.refresh().catch(err => log.error('Failed to refresh WFS layer:', err));
    }, interval);
  }

  /**
   * Stop auto-refresh timer
   */
  private stopAutoRefresh(): void {
    if (this.refreshHandle) {
      this.refreshHandle.cancel();
      this.refreshHandle = null;
    }
  }

  /**
   * Set up click event handler for feature popups
   */
  private setupClickHandler(): void {
    // Remove any existing handler first
    this.cleanupClickHandler();

    // Create click handler
    this.clickHandler = (e: mapboxgl.MapMouseEvent) => {
      // Query features at the clicked point for this layer
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: [this.layerId]
      });

      if (!features || features.length === 0) {
        return;
      }

      // Get the first feature
      const feature = features[0];

      // Close any existing popup
      if (this.currentPopup) {
        this.currentPopup.remove();
      }

      // Format feature properties
      const popupContent = this.formatFeatureProperties(feature);

      // Create and show popup
      this.currentPopup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        className: 'wfs-feature-popup',
        maxWidth: '400px'
      })
        .setLngLat(e.lngLat)
        .setHTML(popupContent)
        .addTo(this.map);

      // Clear reference when popup is closed
      this.currentPopup.on('close', () => {
        this.currentPopup = null;
      });
    };

    // Add click handler to map
    this.map.on('click', this.layerId, this.clickHandler);

    // Change cursor on hover
    this.map.on('mouseenter', this.layerId, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', this.layerId, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  /**
   * Clean up click event handler and popup
   */
  private cleanupClickHandler(): void {
    // Remove popup
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    // Remove click handler
    if (this.clickHandler) {
      this.map.off('click', this.layerId, this.clickHandler);
      this.clickHandler = null;
    }

    // Remove hover cursor handlers (passing the layer as a filter)
    this.map.off('mouseenter', this.layerId, () => {});
    this.map.off('mouseleave', this.layerId, () => {});
  }

  /**
   * Format feature properties into HTML for popup
   *
   * @param feature GeoJSON feature
   * @returns HTML string
   */
  private formatFeatureProperties(feature: mapboxgl.MapboxGeoJSONFeature): string {
    const properties = feature.properties || {};
    const geometryType = feature.geometry.type;

    // Build properties table
    const rows = Object.entries(properties)
      .filter(([key]) => !key.startsWith('_')) // Filter out internal properties
      .map(([key, value]) => {
        // Format the value
        let displayValue = value;
        if (value === null || value === undefined) {
          displayValue = '<em>null</em>';
        } else if (typeof value === 'object') {
          displayValue = JSON.stringify(value);
        } else {
          displayValue = String(value);
        }

        return `
          <tr>
            <td class="property-key">${key}</td>
            <td class="property-value">${displayValue}</td>
          </tr>
        `;
      })
      .join('');

    return `
      <div class="wfs-popup-content">
        <div class="wfs-popup-header">
          <h4>${this.config.name}</h4>
          <span class="geometry-type">${geometryType}</span>
        </div>
        <div class="wfs-popup-body">
          ${rows ? `
            <table class="properties-table">
              <tbody>
                ${rows}
              </tbody>
            </table>
          ` : '<p><em>No properties available</em></p>'}
        </div>
      </div>
    `;
  }
}
